/**
 * ============================================================================
 * ACCOUNT DELETION COMPONENT - GDPR COMPLIANCE
 * ============================================================================
 * 
 * Component for account deletion (GDPR Article 17)
 */

/**
 * ============================================================================
 * ACCOUNT DELETION COMPONENT - 100% Ready (GDPR Compliance)
 * ============================================================================
 * 
 * Component for account deletion (GDPR Article 17)
 */

import { useState } from 'react';
import { deleteAccount, requestAccountDeletion } from '../../services/users';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Textarea from '../common/Textarea';
import Checkbox from '../common/Checkbox';

const AccountDeletion = () => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const { showSuccess, showError } = useToast();
  const { language } = useI18n();

  const handleDelete = async () => {
    if (!confirmDelete) {
      showError(language === 'fr' ? 'Veuillez confirmer la suppression du compte' : 'Please confirm account deletion');
      return;
    }

    setLoading(true);

    try {
      await deleteAccount();
      showSuccess(
        language === 'fr'
          ? 'Votre compte a été supprimé. Vous serez déconnecté.'
          : 'Your account has been deleted. You will be logged out.'
      );

      // Logout and redirect after 2 seconds
      setTimeout(() => {
        localStorage.clear();
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr'
            ? 'Échec de la suppression du compte. Veuillez réessayer.'
            : 'Failed to delete account. Please try again.')
      );
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  return (
    <>
      <div>
        <div className="dashboard-wt-card-header flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-2xl flex-shrink-0">
            ⚠️
          </div>
          <div>
            <h3 className="dashboard-wt-card-title text-[#fca5a5]">
              {language === 'fr' ? 'Supprimer le compte' : 'Delete Account'}
            </h3>
            <p className="dashboard-wt-card-desc text-[rgba(255,255,255,0.8)]">
              {language === 'fr'
                ? 'Action irréversible. Données personnelles anonymisées ; enregistrements financiers conservés 7 ans (légal).'
                : 'Irreversible. Personal data anonymized; financial records retained 7 years (legal).'}
            </p>
          </div>
        </div>

        <div className="dashboard-wt-card-content">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-transparent border-2 border-[#ef4444] hover:bg-[rgba(239,68,68,0.1)] hover:border-[#f87171] transition-all"
          >
            {language === 'fr' ? 'Supprimer mon compte' : 'Delete My Account'}
          </button>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        size="md"
        className="!bg-[var(--wt-black-2)] !text-white border border-[var(--wt-border)]"
        title={
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-xl">
              ⚠️
            </div>
            <span className="text-xl font-bold text-[#fca5a5]">
              {language === 'fr' ? 'Supprimer le compte' : 'Delete Account'}
            </span>
          </div>
        }
      >
        <div className="space-y-6 text-white">
          <div className="p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)]">
            <p className="text-[#fca5a5] font-bold mb-2">
              {language === 'fr' ? 'Attention: action permanente' : 'Warning: permanent action'}
            </p>
            <ul className="text-[rgba(255,255,255,0.9)] text-sm space-y-1 list-disc list-inside">
              <li>{language === 'fr' ? 'Compte supprimé définitivement' : 'Account permanently deleted'}</li>
              <li>{language === 'fr' ? 'Données personnelles anonymisées' : 'Personal data anonymized'}</li>
              <li>{language === 'fr' ? 'Enregistrements financiers conservés 7 ans' : 'Financial records retained 7 years'}</li>
            </ul>
          </div>

          <Textarea
            label={language === 'fr' ? 'Raison (optionnel)' : 'Reason (optional)'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={language === 'fr' ? 'Pourquoi vous partez…' : 'Why you\'re leaving…'}
            rows={3}
            className="!bg-black !border-[var(--wt-border)] !text-white"
          />

          <Input
            label={language === 'fr' ? 'Mot de passe (si applicable)' : 'Password (if applicable)'}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={language === 'fr' ? 'Mot de passe' : 'Password'}
            className="!bg-black !border-[var(--wt-border)] !text-white"
          />

          <Checkbox
            checked={confirmDelete}
            onChange={(e) => setConfirmDelete(e.target.checked)}
            label={
              language === 'fr'
                ? 'Je comprends que cette action est permanente'
                : 'I understand this action is permanent'
            }
          />

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="dashboard-wt-btn-secondary flex-1"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!confirmDelete || loading}
              className="flex-1 py-3 px-4 rounded-lg font-semibold text-white bg-[#dc2626] border border-[#ef4444] hover:bg-[#b91c1c] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (language === 'fr' ? 'Suppression…' : 'Deleting…') : (language === 'fr' ? 'Supprimer le compte' : 'Delete Account')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AccountDeletion;
